import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

interface Partner {
  id: string;
  name: string;
  logo_url: string;
  slug: string;
  access_password?: string;
  needs_password_reset?: boolean;
}

interface Alteration {
  id: string;
  client_name: string;
  service_name: string;
  status: string;
  price: number;
  created_at: string;
}

interface Invoice {
  id: string;
  period_month: number;
  period_year: number;
  total_amount: number;
  status: string;
}

export default function PartnerPortal() {
  const { slug } = useParams<{ slug: string }>();
  const [partner, setPartner] = useState<Partner | null>(null);
  const [alterations, setAlterations] = useState<Alteration[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  
  // Security State
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [loginError, setLoginError] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [isUpdatingPwd, setIsUpdatingPwd] = useState(false);

  useEffect(() => {
    fetchPartnerPortalData();
  }, [slug]);

  const fetchPartnerPortalData = async () => {
    const { data: pData } = await supabase.from('partners').select('*').eq('slug', slug).single();
    if (pData) {
      setPartner(pData);
      
      // Check if session exists in sessionStorage
      const sessionKey = `partner_session_${pData.id}`;
      if (sessionStorage.getItem(sessionKey) === 'true' || !pData.access_password) {
        setIsAuthenticated(true);
        if (pData.needs_password_reset) setShowResetModal(true);
        loadPortalData(pData.id);
      }
    }
    setLoading(false);
  };

  const loadPortalData = async (partnerId: string) => {
    const { data: aData } = await supabase.from('alterations').select('*').eq('partner_id', partnerId).order('created_at', { ascending: false });
    const { data: iData } = await supabase.from('partner_invoices').select('*').eq('partner_id', partnerId).order('created_at', { ascending: false });
    if (aData) setAlterations(aData);
    if (iData) setInvoices(iData);
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (partner?.access_password === passwordInput) {
      setIsAuthenticated(true);
      sessionStorage.setItem(`partner_session_${partner.id}`, 'true');
      if (partner.needs_password_reset) setShowResetModal(true);
      loadPortalData(partner.id);
      setLoginError(false);
    } else {
      setLoginError(true);
      setPasswordInput('');
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPassword || !partner) return;
    setIsUpdatingPwd(true);
    const { error } = await supabase
      .from('partners')
      .update({ 
        access_password: newPassword, 
        needs_password_reset: false 
      })
      .eq('id', partner.id);
    
    if (!error) {
      setShowResetModal(false);
      setPartner({ ...partner, access_password: newPassword, needs_password_reset: false });
    }
    setIsUpdatingPwd(false);
  };

  const copyBookingLink = () => {
    const link = `${window.location.origin}/partner/${partner?.slug}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  if (loading) return <div className="min-h-screen bg-white flex items-center justify-center"><i className="ri-loader-4-line animate-spin text-3xl text-brand-gray-300" /></div>;
  if (!partner) return <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6 text-center"><h1 className="font-serif text-2xl mb-2">Acceso no autorizado</h1><Link to="/" className="bg-brand-black text-white px-8 py-3 rounded-full text-sm mt-4">Volver al Inicio</Link></div>;

  // LOGIN SCREEN
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-brand-gray-50 flex items-center justify-center p-6 font-sans">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white w-full max-w-md p-12 rounded-[48px] shadow-2xl border border-brand-gray-100 text-center space-y-10">
          <div className="space-y-4">
            <div className="w-20 h-20 bg-brand-cream rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm border border-brand-gray-50 overflow-hidden">
              {partner.logo_url ? <img src={partner.logo_url} alt={partner.name} className="w-full h-full object-cover" /> : <i className="ri-lock-password-line text-brand-sage text-3xl" />}
            </div>
            <p className="text-[10px] uppercase tracking-[0.4em] text-brand-gray-400 font-bold">Área Privada de Partner</p>
            <h2 className="font-serif text-3xl text-brand-black italic tracking-tighter">{partner.name}</h2>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2 text-left">
              <label className="text-[10px] uppercase tracking-widest text-brand-gray-400 font-bold ml-4">Contraseña de Acceso</label>
              <input 
                type="password" 
                placeholder="Introducir clave..." 
                className={`w-full bg-brand-gray-50 border-none rounded-2xl px-6 py-4 text-center text-sm focus:ring-2 transition-all ${loginError ? 'ring-2 ring-red-200' : 'focus:ring-brand-sage/20'}`}
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                autoFocus
              />
              {loginError && <p className="text-[10px] text-red-500 text-center font-bold uppercase tracking-widest mt-2 animate-bounce">Contraseña incorrecta</p>}
            </div>
            <button type="submit" className="w-full bg-brand-black text-white py-4 rounded-2xl text-[10px] uppercase tracking-[0.3em] font-bold hover:bg-brand-gray-800 transition-all shadow-xl shadow-black/10">Entrar al Portal</button>
          </form>

          <p className="text-[9px] text-brand-gray-400 italic">Si has olvidado tu contraseña, contacta con Anthony Sánchez Atelier.</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFDFD]">
      <Navbar />
      <main className="pt-32 pb-24 px-6">
        <div className="max-w-6xl mx-auto space-y-16">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 border-b border-brand-gray-100 pb-12">
            <div className="flex items-center gap-8 text-left">
              <div className="w-24 h-24 rounded-full bg-brand-cream flex items-center justify-center overflow-hidden border border-brand-gray-50 shadow-sm">
                {partner.logo_url ? <img src={partner.logo_url} alt={partner.name} className="w-full h-full object-cover" /> : <i className="ri-building-line text-brand-sage text-3xl" />}
              </div>
              <div className="space-y-1">
                <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-brand-gray-400 font-medium">Panel de Colaboración Privado</p>
                <h1 className="font-serif text-4xl text-brand-black tracking-tight italic">{partner.name}</h1>
                <p className="font-sans text-xs text-brand-sage italic font-medium">Atelier Anthony Sánchez × Exclusive Partner</p>
              </div>
            </div>
            <div className="flex gap-4">
              <button 
                onClick={copyBookingLink}
                className={`flex items-center gap-2 px-8 py-3 rounded-full text-[10px] uppercase tracking-[0.2em] transition-all border font-bold ${
                  copied ? 'bg-brand-sage border-brand-sage text-white' : 'bg-black border-black text-white hover:bg-brand-gray-800'
                }`}
              >
                <i className={copied ? "ri-check-line" : "ri-links-line"} />
                {copied ? 'Enlace Copiado' : 'Enlace de Reserva'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
            {/* Stats */}
            <div className="lg:col-span-1 space-y-8">
              <div className="space-y-2 text-left">
                <p className="text-[9px] uppercase tracking-[0.3em] text-brand-gray-400 font-bold ml-4">Resumen de Actividad</p>
                <div className="bg-brand-gray-50 p-8 rounded-[40px] border border-brand-gray-100">
                  <p className="text-brand-gray-400 text-[10px] uppercase tracking-widest mb-1">Total Arreglos</p>
                  <h3 className="text-4xl font-serif text-brand-black italic">{alterations.length}</h3>
                </div>
              </div>
              <div className="bg-white p-8 rounded-[40px] border border-brand-gray-100 shadow-sm text-left">
                <p className="text-brand-gray-400 text-[10px] uppercase tracking-widest mb-1">Pendiente Cierre</p>
                <h3 className="text-4xl font-serif text-brand-black italic">
                  {alterations.filter(a => a.status === 'completed').length}
                </h3>
                <p className="text-[9px] text-brand-sage mt-2 italic font-serif font-bold">Pendiente de facturar</p>
              </div>
            </div>

            {/* Invoices List */}
            <div className="lg:col-span-3 space-y-6">
              <div className="flex items-center justify-between px-4">
                <h2 className="font-serif text-2xl tracking-tight italic">Facturación Mensual</h2>
                <span className="text-[10px] uppercase tracking-[0.3em] text-brand-gray-400 font-medium">Histórico B2B</span>
              </div>
              <div className="bg-white rounded-[40px] border border-brand-gray-100 shadow-sm overflow-hidden">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-brand-gray-50">
                      <th className="px-10 py-6 text-[9px] uppercase tracking-[0.3em] text-brand-gray-400 font-bold">Periodo Facturado</th>
                      <th className="px-10 py-6 text-[9px] uppercase tracking-[0.3em] text-brand-gray-400 font-bold text-center">Importe Total</th>
                      <th className="px-10 py-6 text-[9px] uppercase tracking-[0.3em] text-brand-gray-400 font-bold text-center">Estado</th>
                      <th className="px-10 py-6 text-[9px] uppercase tracking-[0.3em] text-brand-gray-400 font-bold text-right">Detalle</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-gray-50">
                    {invoices.map(inv => (
                      <tr key={inv.id} className="hover:bg-brand-gray-50/50 transition-colors">
                        <td className="px-10 py-6 font-sans text-sm text-brand-black font-medium capitalize">
                          {new Date(0, inv.period_month-1).toLocaleString('es', { month: 'long' })} {inv.period_year}
                        </td>
                        <td className="px-10 py-6 font-serif text-lg text-brand-black text-center">{inv.total_amount.toFixed(2)}€</td>
                        <td className="px-10 py-6 text-center">
                          <span className={`px-4 py-1.5 rounded-full text-[9px] uppercase tracking-[0.2em] font-bold ${
                            inv.status === 'paid' ? 'bg-brand-sage/10 text-brand-sage' : 'bg-amber-50 text-amber-600'
                          }`}>
                            {inv.status === 'paid' ? 'Pagada' : 'Pendiente de Pago'}
                          </span>
                        </td>
                        <td className="px-10 py-6 text-right">
                          <Link 
                            to={`/invoice/${inv.id}`}
                            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-brand-black hover:text-brand-sage font-bold transition-colors"
                          >
                            Ver Factura <i className="ri-external-link-line" />
                          </Link>
                        </td>
                      </tr>
                    ))}
                    {invoices.length === 0 && (
                      <tr><td colSpan={4} className="px-10 py-20 text-center text-brand-gray-300 font-sans text-sm italic">Aún no hay facturas emitidas para este partner.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Alterations History */}
          <div className="space-y-6 pt-12">
            <div className="flex items-center justify-between px-4">
              <h2 className="font-serif text-2xl tracking-tight italic">Registro de Arreglos</h2>
              <span className="text-[10px] uppercase tracking-[0.3em] text-brand-gray-400 font-medium">Control de Calidad</span>
            </div>
            <div className="bg-white rounded-[40px] border border-brand-gray-100 shadow-sm overflow-hidden">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-brand-gray-50">
                    <th className="px-10 py-6 text-[9px] uppercase tracking-[0.3em] text-brand-gray-400 font-bold">Prenda / Cliente</th>
                    <th className="px-10 py-6 text-[9px] uppercase tracking-[0.3em] text-brand-gray-400 font-bold">Servicio Realizado</th>
                    <th className="px-10 py-6 text-[9px] uppercase tracking-[0.3em] text-brand-gray-400 font-bold text-center">Estado</th>
                    <th className="px-10 py-6 text-[9px] uppercase tracking-[0.3em] text-brand-gray-400 font-bold text-right">Fecha</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-gray-50">
                  {alterations.map(alt => (
                    <tr key={alt.id} className="hover:bg-brand-gray-50/50 transition-colors">
                      <td className="px-10 py-6">
                        <p className="font-sans text-sm text-brand-black font-medium">{alt.client_name}</p>
                      </td>
                      <td className="px-10 py-6">
                        <p className="font-sans text-xs text-brand-gray-600">{alt.service_name}</p>
                      </td>
                      <td className="px-10 py-6 text-center">
                        <span className={`px-4 py-1 rounded-full text-[9px] uppercase tracking-[0.1em] font-bold ${
                          alt.status === 'completed' ? 'bg-brand-sage/10 text-brand-sage' : 
                          alt.status === 'invoiced' ? 'bg-brand-black text-white' : 'bg-brand-gray-100 text-brand-gray-400'
                        }`}>
                          {alt.status === 'pending' ? 'Pendiente' : alt.status === 'completed' ? 'Completado' : 'Facturado'}
                        </span>
                      </td>
                      <td className="px-10 py-6 text-right font-sans text-[11px] text-brand-gray-400">
                        {new Date(alt.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
      <Footer />

      {/* PASSWORD RESET MODAL */}
      <AnimatePresence>
        {showResetModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/60 backdrop-blur-md">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white w-full max-w-sm p-10 rounded-[48px] shadow-2xl text-center space-y-8">
              <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-keyhole-line text-amber-600 text-2xl" />
              </div>
              <div className="space-y-2">
                <h3 className="font-serif text-2xl text-brand-black tracking-tight">Seguridad Requerida</h3>
                <p className="text-xs text-brand-gray-500 leading-relaxed px-4">Por favor, establece una nueva contraseña personalizada para acceder a tu portal a partir de ahora.</p>
              </div>
              <form onSubmit={handleUpdatePassword} className="space-y-6">
                <input 
                  type="password" 
                  placeholder="Nueva contraseña..." 
                  className="w-full bg-brand-gray-50 border-none rounded-2xl px-6 py-4 text-center text-sm focus:ring-2 focus:ring-brand-sage/20 transition-all font-mono"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoFocus
                />
                <button 
                  type="submit" 
                  disabled={isUpdatingPwd}
                  className="w-full bg-brand-black text-white py-4 rounded-2xl text-[10px] uppercase tracking-[0.3em] font-bold hover:bg-brand-gray-800 transition-all disabled:opacity-50"
                >
                  {isUpdatingPwd ? 'Actualizando...' : 'Actualizar y Entrar'}
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
